/* istanbul ignore file */

import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { IllusEmpty_1, IllusLocation } from '@garnish/core';
import { useSelector } from '@xstate/react';

import { useLocationSearchFlags } from '../../../../hooks/useLocationSearchFlags';
import { useLocationSearchVisibleLocations } from '../../../../hooks/useLocationSearchVisibleLocations';
import { usePredictions } from '../../../../hooks/usePredictions';
import { type LocationSearchService } from '../../../../machine';
import { LocationResults, LocationResultsListsContainer } from '../../../LocationResults';
import { LocationResultsListInfoMessage } from '../../../LocationResultsListInfoMessage';
import { LocationSearchPredictionsList } from '../../../LocationSearchPredictionsList';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchLocationsResults = memo((props: LocationSearchLocationsResultsProps) => {
  const {
    locationSearchActorRef,
    interactedLocationRestaurantSlug,
    withSafeAreaInsets,
    onExpandedChange,
  } = props;

  const { formatMessage } = useIntl();

  const { shouldRenderPredictions } = usePredictions({ locationSearchActorRef });

  const { isLoadingLocations } = useLocationSearchFlags({ locationSearchActorRef });

  const locations = useLocationSearchVisibleLocations({ locationSearchActorRef });

  const locationSearchType = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.locationSearchType,
  );

  // ─── Derived ─────────────────────────────────────────────────────────

  const { pickup: pickupLocations = [], outpost: outpostLocations = [] } = locations ?? {};

  const activeLocations = locationSearchType === 'outpost' ? outpostLocations : pickupLocations;
  const otherLocations = locationSearchType === 'outpost' ? pickupLocations : outpostLocations;

  const hasResolvedLocations = locations !== undefined;
  const noLocationsFound =
    hasResolvedLocations && activeLocations.length + otherLocations.length === 0;

  const shouldRenderIntroMessage = !isLoadingLocations && !hasResolvedLocations;
  const shouldRenderNoLocationsFoundMessage = !isLoadingLocations && noLocationsFound;

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderPredictions) {
    return (
      <LocationSearchPredictionsList
        locationSearchActorRef={locationSearchActorRef}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
      />
    );
  }

  if (shouldRenderIntroMessage) {
    return (
      <LocationResultsListInfoMessage
        illustration={IllusLocation}
        illustrationAriaLabel={formatMessage(messages.locationLocationIllustrationAlt)}
        text={formatMessage(messages.locationEnableLocationServices)}
      />
    );
  }

  if (shouldRenderNoLocationsFoundMessage) {
    return (
      <LocationResultsListInfoMessage
        illustration={IllusEmpty_1}
        illustrationAriaLabel={formatMessage(messages.locationEmptyResultsIllustrationAlt)}
        title={formatMessage(messages.locationNoResultsBanner)}
      />
    );
  }

  return (
    <LocationResultsListsContainer>
      <LocationResults
        locationSearchActorRef={locationSearchActorRef}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        withSafeAreaInsets={withSafeAreaInsets}
        onExpandedChange={onExpandedChange}
      />
    </LocationResultsListsContainer>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationLocationIllustrationAlt: {
    defaultMessage: 'Sweetgreen customer with the location pin',
    description: 'Location | Location Illustration | Alt',
  },
  locationEnableLocationServices: {
    defaultMessage: 'Enter an address above to get started.',
    description: 'Location | Search results placeholder',
  },
  locationEmptyResultsIllustrationAlt: {
    defaultMessage: 'Seedless pea pod',
    description: 'Location | Empty Results Illustration | Alt',
  },
  locationNoResultsBanner: {
    defaultMessage: `Oops, we couldn't find any sweetgreen locations. Try another search`,
    description: 'Location | No Results | Banner',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchLocationsResultsProps = {
  locationSearchActorRef: LocationSearchService; // prettier-ignore
  interactedLocationRestaurantSlug: string | undefined;
  withSafeAreaInsets?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
};
